'use client';

import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useTranslations } from '@/i18n/use-translations';
import { IS_CA, IS_FR } from '@/utils/constants';
import { env } from '@/utils/env';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { accDiv, floorNumber } from '@/utils/tools';
import { CloseOutlined } from '@zeelool/icon';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BNPL_PRODUCT_INFO_BY_SITE, DEFAULT_BNPL_PRODUCT_INFO } from './bnplLearnMoreProductInfo';
import type { BnplLearnMoreSite, BnplProductInfo } from './bnplLearnMoreProductInfo';

interface BnplLearnMoreDialogProps {
  open: boolean;
  products?: string[] | null;
  amount?: string | number;
  currency?: Parameters<typeof formatPriceNumberWithSymbol>[1];
  onClose: () => void;
}

const getProductInfoConfig = (siteName?: string | null) => {
  const key = siteName?.toLowerCase() as BnplLearnMoreSite | undefined;

  return (key && BNPL_PRODUCT_INFO_BY_SITE[key]) || DEFAULT_BNPL_PRODUCT_INFO;
};

const PRODUCT_DISPLAY_ORDER = ['klarna', 'afterpay', 'clearpay', 'paypal'];

const getProductDisplayOrder = (key: string) => {
  const index = PRODUCT_DISPLAY_ORDER.indexOf(key);

  return index === -1 ? PRODUCT_DISPLAY_ORDER.length : index;
};

const getProductsInfo = (products?: string[] | null): BnplProductInfo[] => {
  const productInfoConfig = getProductInfoConfig(env.NEXT_PUBLIC_SITE_NAME);
  // 暂时默认展示全部，保留 products 过滤逻辑方便恢复。//
  // const showAllProducts = true;

  // if (showAllProducts) {
  //   return Object.values(productInfoConfig).sort(
  //     (prev, next) => getProductDisplayOrder(prev.key) - getProductDisplayOrder(next.key),
  //   );
  // }
  // ==== //

  const info = new Map<string, BnplProductInfo>();

  products?.forEach((product) => {
    const key = product?.trim().toLowerCase();
    const productInfo = productInfoConfig[key];

    if (productInfo && !info.has(productInfo.key)) {
      info.set(productInfo.key, productInfo);
    }
  });

  return Array.from(info.values());
};

/**
 * 现在只知道 ca 是 4， uk 是3，其他的还未知。
 */
const KLARNA_INSTALLMENT_COUNT = IS_CA ? 4 : 3;

const getKlarnaInstallmentPrice = (
  amount?: string | number,
  currency?: Parameters<typeof formatPriceNumberWithSymbol>[1],
  installmentCount = KLARNA_INSTALLMENT_COUNT,
) => {
  if (!amount) {
    return '';
  }

  const price = floorNumber(accDiv(amount, installmentCount), 2);

  return formatPriceNumberWithSymbol(price, currency);
};

const getTitle = (
  productKey: string,
  title: string,
  amount?: string | number,
  currency?: Parameters<typeof formatPriceNumberWithSymbol>[1],
  installmentAmount?: string | number,
  installmentCount = KLARNA_INSTALLMENT_COUNT,
) => {
  if (productKey !== 'klarna') {
    return title;
  }

  const price = installmentAmount
    ? formatPriceNumberWithSymbol(installmentAmount.toString(), currency)
    : getKlarnaInstallmentPrice(amount, currency, installmentCount);

  return `${IS_FR ? '' : installmentCount} ${title}${price ? ` ${price}` : ''}`;
};

const getProductContent = (product?: BnplProductInfo) => ({
  title: product?.title || '',
  subTitle: product?.subTitle || '',
  description: product?.description || '',
  steps: product?.steps || [],
  legal: product?.legal || [],
});
const PRODUCT_ANCHOR_TOP_OFFSET = 35;

export const renderBnplProductDetails = ({
  site,
  productKey,
  amount,
  currency,
  installmentAmount,
  installmentCount,
  from,
  isWeb,
}: {
  site: string;
  productKey: string;
  amount?: string | number;
  currency?: Parameters<typeof formatPriceNumberWithSymbol>[1];
  installmentAmount?: string | number;
  installmentCount?: number;
  from?: string;
  isWeb?: boolean;
}) => {
  const config = getProductInfoConfig(site);
  const productInfo = config[productKey?.trim().toLowerCase()];
  if (!productInfo) return null;

  const productContent = getProductContent(productInfo);

  if (!isWeb) {
    return (
      <>
        <Image
          src={productInfo.logo}
          alt={productInfo.label}
          width={50}
          height={22}
          className={`${from === 'payment' ? 'w-[100px] h-[44px] block mx-auto mt-[-20px] mb-[10px]' : 'mb-[11px] h-[22px] w-[50px] object-contain'}`}
        />
        {productContent.title && (
          <h3
            className={`${from === 'payment' ? 'mt-[21px] text-[22px] font-bs text-primary-black leading-[30px] notranslate' : 'text-[14px] font-be leading-[17px] text-primary-text'}`}
          >
            {getTitle(
              productInfo.key,
              productContent.title,
              amount,
              currency,
              installmentAmount,
              installmentCount,
            )}
          </h3>
        )}
        {productContent.subTitle && (
          <p
            className={`mt-[2px] leading-[18px] text-primary-black ${from === 'payment' ? 'text-[13px]' : 'text-[12px]'}`}
            dangerouslySetInnerHTML={{ __html: productContent.subTitle }}
          />
        )}
        {productContent.description && (
          <p
            className={`mt-[2px] leading-[18px] text-primary-black ${from === 'payment' ? 'text-[13px]' : 'text-[12px]'}`}
            dangerouslySetInnerHTML={{ __html: productContent.description }}
          />
        )}
        {productContent.steps.length > 0 && (
          <ul className="space-y-[5px]">
            {productContent.steps.map((step) => (
              <li key={step} className="flex text-[12px] leading-[20px] text-primary-text">
                <span className="mt-[5.5px] mr-[8px] h-[8px] w-[8px] shrink-0 bg-primary-black" />
                <p dangerouslySetInnerHTML={{ __html: step }}></p>
              </li>
            ))}
          </ul>
        )}
        {productContent.legal.length > 0 && (
          <div className="mt-[12px] text-[12px] leading-[18px] text-auxiliary-text">
            {productContent.legal.map((paragraph) => (
              <p
                className="mb-10"
                key={paragraph}
                dangerouslySetInnerHTML={{ __html: paragraph }}
              ></p>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <Image
        src={productInfo.logo}
        alt={productInfo.label}
        width={78}
        height={34}
        className={`${from === 'payment' ? 'mt-[-30px] mb-[12px] h-[68px] w-[156px] object-contain mx-auto' : 'mb-[12px] h-[34px] w-[78px] object-contain'}`}
      />
      {productContent.title && (
        <h3
          className={`${from === 'payment' ? 'text-[30px] font-bb text-primary-black leading-[40px] text-center' : 'text-[18px] font-bs leading-[22px] text-primary-text'}`}
        >
          {getTitle(
            productInfo.key,
            productContent.title,
            amount,
            currency,
            installmentAmount,
            installmentCount,
          )}
        </h3>
      )}
      {productContent.subTitle && (
        <p
          className={`${from === 'payment' ? 'text-secondary-text text-[16px] font-br text-center leading-[20px] mt-[10px]' : 'mt-[4px] text-[14px] leading-[20px] text-primary-black'}`}
          dangerouslySetInnerHTML={{ __html: productContent.subTitle }}
        />
      )}
      {productContent.description && (
        <p
          className="mt-[4px] text-[14px] leading-[20px] text-primary-black"
          dangerouslySetInnerHTML={{ __html: productContent.description }}
        />
      )}
      {productContent.steps.length > 0 && (
        <ul>
          {productContent.steps.map((step) => (
            <li key={step} className="flex text-[14px] leading-[28px] text-primary-text">
              <span className="mt-[10px] mr-[5px] h-[8px] w-[8px] shrink-0 bg-primary-black" />
              <p dangerouslySetInnerHTML={{ __html: step }}></p>
            </li>
          ))}
        </ul>
      )}
      {productContent.legal.length > 0 && (
        <div className={`mt-[28px] text-[14px]  leading-[21px] text-secondary-text`}>
          {productContent.legal.map((paragraph) => (
            <p
              key={paragraph}
              dangerouslySetInnerHTML={{ __html: paragraph }}
              className="mb-10"
            ></p>
          ))}
        </div>
      )}
    </>
  );
};

export default function BnplLearnMoreDialog({
  open,
  products,
  amount,
  currency,
  onClose,
}: BnplLearnMoreDialogProps) {
  const t = useTranslations('payment.BnplLearnMore');
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const productsInfo = useMemo(() => getProductsInfo(products), [products]);
  const dialogTitle = t('title');
  const [activeKey, setActiveKey] = useState('');
  const [isProductsScrolling, setIsProductsScrolling] = useState(false);
  const activeProduct =
    productsInfo.find((product) => product.key === activeKey) || productsInfo[0];
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const anchorRefs = useRef<Record<string, HTMLElement | null>>({});
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const programmaticScrollTargetRef = useRef<{ top: number } | null>(null);
  const programmaticScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollbarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const content = useMemo(() => getProductContent(activeProduct), [activeProduct]);

  useEffect(() => {
    if (!productsInfo.some((product) => product.key === activeKey)) {
      setActiveKey(productsInfo[0]?.key || '');
    }
  }, [activeKey, productsInfo]);

  useEffect(
    () => () => {
      if (programmaticScrollTimerRef.current) {
        clearTimeout(programmaticScrollTimerRef.current);
      }

      if (scrollbarTimerRef.current) {
        clearTimeout(scrollbarTimerRef.current);
      }
    },
    [],
  );

  const handleProductClick = (key: string) => {
    setActiveKey(key);

    if (!isWeb) {
      return;
    }

    const container = scrollContainerRef.current;
    const anchor = anchorRefs.current[key];

    if (!container || !anchor) {
      return;
    }

    const anchorTop = Math.max(anchor.offsetTop - PRODUCT_ANCHOR_TOP_OFFSET, 0);

    if (programmaticScrollTimerRef.current) {
      clearTimeout(programmaticScrollTimerRef.current);
    }

    programmaticScrollTargetRef.current = { top: anchorTop };
    programmaticScrollTimerRef.current = setTimeout(() => {
      programmaticScrollTargetRef.current = null;
      programmaticScrollTimerRef.current = null;
    }, 500);

    container.scrollTo({
      top: anchorTop,
      behavior: 'smooth',
    });
  };

  const handleProductsScroll = () => {
    const container = scrollContainerRef.current;

    if (!container) {
      return;
    }

    setIsProductsScrolling(true);

    if (scrollbarTimerRef.current) {
      clearTimeout(scrollbarTimerRef.current);
    }

    scrollbarTimerRef.current = setTimeout(() => {
      setIsProductsScrolling(false);
      scrollbarTimerRef.current = null;
    }, 500);

    const threshold = 24;
    const programmaticScrollTarget = programmaticScrollTargetRef.current;

    if (programmaticScrollTarget) {
      const isTargetReached =
        Math.abs(container.scrollTop - programmaticScrollTarget.top) <= threshold;
      const isScrolledToBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;

      if (isTargetReached || isScrolledToBottom) {
        programmaticScrollTargetRef.current = null;

        if (programmaticScrollTimerRef.current) {
          clearTimeout(programmaticScrollTimerRef.current);
          programmaticScrollTimerRef.current = null;
        }
      }

      return;
    }

    const isScrolledToBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
    const nextActiveProduct = isScrolledToBottom
      ? productsInfo[productsInfo.length - 1]
      : productsInfo.reduce<BnplProductInfo | null>((current, product) => {
          const anchor = anchorRefs.current[product.key];

          if (!anchor) {
            return current;
          }

          const anchorTop = Math.max(anchor.offsetTop - PRODUCT_ANCHOR_TOP_OFFSET, 0);

          return anchorTop <= container.scrollTop + threshold ? product : current;
        }, productsInfo[0] || null);

    if (nextActiveProduct && nextActiveProduct.key !== activeKey) {
      setActiveKey(nextActiveProduct.key);
    }
  };

  if (!isWeb) {
    return (
      <Popup
        disableScrollLock={false}
        open={open && productsInfo.length > 0}
        onClose={() => {
          setActiveKey('');
          onClose();
        }}
        title={dialogTitle}
        showDivider={false}
        borderRadius={16}
        height="68vh"
        childrenMaxHeight="calc(68vh - 52px)"
        style={{
          maxHeight: 'calc(100vh - 40px)',
        }}
        sx={{
          '& .popup-content': {
            padding: 0,
          },
        }}
      >
        {activeProduct && (
          <div className="flex h-full flex-col text-primary-text">
            <div className="flex shrink-0 items-end border-b border-auxiliary-light-gray-blue">
              <div className="grid w-full grid-cols-3 items-end" role="tablist">
                {productsInfo.map((product) => (
                  <button
                    key={product.key}
                    type="button"
                    role="tab"
                    aria-selected={product.key === activeKey}
                    className={`relative flex h-[25px] min-w-0 items-start justify-center text-[16px] leading-[20px] cursor-pointer ${
                      product.key === activeKey
                        ? 'font-bs text-primary-text after:absolute after:bottom-[-1px] after:left-1/2 after:h-[1.5px] after:w-[50px] after:-translate-x-1/2 after:bg-primary-black after:content-[""]'
                        : 'font-br text-auxiliary-text'
                    }`}
                    onClick={() => handleProductClick(product.key)}
                  >
                    {product.label}
                  </button>
                ))}
              </div>
            </div>

            <section className="flex-1 overflow-y-auto px-20 pb-[24px] pt-[24px] text-[14px] text-primary-text">
              {renderBnplProductDetails({
                site: env.NEXT_PUBLIC_SITE_NAME,
                productKey: activeProduct.key,
                amount,
                currency,
                isWeb,
              })}
            </section>
          </div>
        )}
      </Popup>
    );
  }

  return (
    <Dialog
      open={open && productsInfo.length > 0}
      onClose={() => {
        setActiveKey('');
        onClose();
      }}
      hideHeader
      ariaLabel={dialogTitle}
      footer={null}
      disableScrollLock={false}
      sx={{
        '& .MuiDialog-paper': {
          width: '680px',
          height: '552px',
          maxWidth: 'calc(100vw - 40px)',
          borderRadius: '10px',
          boxShadow: 'none',
        },
        '& .MuiDialogContent-root': {
          padding: 0,
        },
      }}
    >
      <div className="relative h-full px-[24px] pb-[36px] pt-[24px] text-primary-text">
        <h2 className="text-center text-[20px] font-bs leading-[24px]">{dialogTitle}</h2>
        <button
          type="button"
          aria-label="Close"
          className="absolute right-[16px] top-[16px] flex h-[24px] w-[24px] items-center justify-center text-primary-black cursor-pointer"
          onClick={() => {
            setActiveKey('');
            onClose();
          }}
        >
          <CloseOutlined className="text-[24px]" />
        </button>

        <div className="mt-[32px] flex h-[34px] items-start border-b border-auxiliary-light-gray">
          <div className="flex items-start gap-[48px]" role="tablist">
            {productsInfo.map((product) => (
              <button
                key={product.key}
                ref={(node) => {
                  tabRefs.current[product.key] = node;
                }}
                type="button"
                role="tab"
                aria-selected={product.key === activeKey}
                className={`relative h-[34px] text-[16px] leading-[20px] cursor-pointer ${
                  product.key === activeKey
                    ? 'font-bs text-primary-text after:absolute after:bottom-[-1px] after:left-0 after:h-[2px] after:w-full after:bg-primary-black after:content-[""]'
                    : 'font-br text-secondary-text'
                }`}
                onClick={() => handleProductClick(product.key)}
              >
                {product.label}
              </button>
            ))}
          </div>
        </div>

        <section
          ref={scrollContainerRef}
          className={`relative h-[369px] overflow-y-auto pr-[4px] pt-[35px] ${
            isProductsScrolling ? '' : 'scrollbar-hidden'
          }`}
          onScroll={handleProductsScroll}
        >
          {productsInfo.map((product) => {
            return (
              <div
                key={product.key}
                ref={(node) => {
                  anchorRefs.current[product.key] = node;
                }}
                className="pb-[36px] last:pb-[160px]"
              >
                {renderBnplProductDetails({
                  site: env.NEXT_PUBLIC_SITE_NAME,
                  productKey: product.key,
                  amount,
                  currency,
                  isWeb,
                })}
              </div>
            );
          })}
        </section>
      </div>
    </Dialog>
  );
}
